import React, { useEffect, useState } from 'react'

import apiClient from '@disco/api/server'
import {
  ADDON_SEATS,
  ADDON_STORAGE,
} from '@disco/constants/billing-provider-components'
import { ChargebeeAddOnDetailed } from '@disco/types/types/subscription'
import captureError from '@disco/utils/captureError'
import { getChargebeeAddon } from '@disco/utils/chargebee'
import { formatCurrency } from '@disco/utils/pricing'

import { Box, Text } from 'components/ds'
import Skeleton from 'components/ui/skeleton'

import { useSelector } from 'hooks/use-redux'

export function SeatsAndTracksChargebeePricingChange({
  subscriptionPeriod,
  seatsQuantity,
  tracksQuantity,
  currentUserAllowance,
  currentTrackAllowance,
  shouldFetchPricingChange,
}: {
  subscriptionPeriod: 'monthly' | 'annual' | null | undefined
  seatsQuantity: number
  tracksQuantity: number
  currentUserAllowance: number
  currentTrackAllowance: number
  shouldFetchPricingChange: boolean
}) {
  const { isLoading, error, proratedAdjustmentInCents, recurringCostInCents } =
    useChargebeePricingChange({
      seatsQuantity,
      tracksQuantity,
      currentUserAllowance,
      currentTrackAllowance,
      shouldFetchPricingChange,
    })

  if (isLoading) {
    return (
      <Box height="20px" width="320px">
        <Skeleton type="block" />
      </Box>
    )
  }

  if (error) {
    return (
      <div>
        <Text color="red">Something went wrong</Text>
      </div>
    )
  }

  return (
    <div>
      <SeatsAndTracksChargebeePricingChangeRefundOrCredit
        proratedAdjustmentInCents={proratedAdjustmentInCents}
      />
      <SeatsAndTracksChargebeePricingChangeRecurringCost
        subscriptionPeriod={subscriptionPeriod}
        recurringCostInCents={recurringCostInCents}
      />
    </div>
  )
}

function SeatsAndTracksChargebeePricingChangeRefundOrCredit({
  proratedAdjustmentInCents,
}: {
  proratedAdjustmentInCents: number
}) {
  if (proratedAdjustmentInCents === 0) {
    return null
  }

  return (
    <p className="SeatsAndTracks_RefundedDescription">
      <span>
        <span>
          <span>
            {proratedAdjustmentInCents > 0
              ? 'This change will charge '
              : 'This change will credit '}
          </span>
          {formatCurrency(Math.abs(proratedAdjustmentInCents))}
        </span>{' '}
        to your account, including any applicable sales taxes.
      </span>
    </p>
  )
}

function SeatsAndTracksChargebeePricingChangeRecurringCost({
  subscriptionPeriod,
  recurringCostInCents,
}: {
  subscriptionPeriod: 'monthly' | 'annual' | null | undefined
  recurringCostInCents: number
}) {
  if (recurringCostInCents === 0) {
    return null
  }

  return (
    <p className="SeatsAndTracks_RefundedDescription">
      <span>
        {`This change will ${
          recurringCostInCents > 0 ? 'increase' : 'decrease'
        } your ${subscriptionPeriod || 'subscription'} cost by ${formatCurrency(
          Math.abs(recurringCostInCents),
        )}`}
      </span>
    </p>
  )
}

function useChargebeePricingChange({
  seatsQuantity,
  tracksQuantity,
  currentUserAllowance,
  currentTrackAllowance,
  shouldFetchPricingChange,
}: {
  seatsQuantity: number
  tracksQuantity: number
  currentUserAllowance: number
  currentTrackAllowance: number
  shouldFetchPricingChange: boolean
}) {
  const billingProviderComponents = useSelector(
    state => state.billingProviderComponents,
  )

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [proratedAdjustmentInCents, setProratedAdjustmentInCents] = useState(0)
  const [recurringCostInCents, setRecurringCostInCents] = useState(0)

  useEffect(() => {
    if (!shouldFetchPricingChange) {
      return
    }

    setIsLoading(true)

    async function requestAddonsEstimate() {
      try {
        const [currentEstimate, nextEstimate] = await Promise.all([
          fetchChargebeeAddonsEstimate({
            seatsQuantity: currentUserAllowance,
            tracksQuantity: currentTrackAllowance,
            allAddons: billingProviderComponents.addons,
          }),
          fetchChargebeeAddonsEstimate({
            seatsQuantity,
            tracksQuantity,
            allAddons: billingProviderComponents.addons,
          }),
        ])

        setProratedAdjustmentInCents(
          nextEstimate.prorated_estimate.prorated_adjustment_in_cents,
        )

        const differenceRecurringCostInCents =
          nextEstimate.recurring_estimate.total -
          currentEstimate.recurring_estimate.total

        setRecurringCostInCents(differenceRecurringCostInCents)
      } catch (error) {
        setError(error)
        captureError(error, {
          moduleName: 'useChargebeePricingChange',
        })
      } finally {
        setIsLoading(false)
      }
    }

    requestAddonsEstimate()
  }, [
    seatsQuantity,
    tracksQuantity,
    currentUserAllowance,
    currentTrackAllowance,
    billingProviderComponents.addons,
    shouldFetchPricingChange,
  ])

  return { isLoading, error, proratedAdjustmentInCents, recurringCostInCents }
}

function fetchChargebeeAddonsEstimate({
  seatsQuantity,
  tracksQuantity,
  allAddons,
}: {
  seatsQuantity: number
  tracksQuantity: number
  allAddons: Record<string, ChargebeeAddOnDetailed[]>
}) {
  return apiClient.chargebeeEstimateAddons({
    addons: [
      getChargebeeAddon({
        type: ADDON_SEATS,
        allocated_quantity: seatsQuantity,
        allAddons,
      }),

      getChargebeeAddon({
        type: ADDON_STORAGE,
        allocated_quantity: tracksQuantity,
        allAddons,
      }),
    ].flat(),
    coupon: null,
  })
}
